import type { ProgrammeStatus } from "@/types";

/**
 * Compute programme status based on dates.
 * This is a client-side fallback - the backend should already return computed status.
 */
export function computeProgrammeStatus(startDate: string, endDate: string): ProgrammeStatus {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (now < start) return "not_started";
  if (now > end) return "completed";
  return "in_progress";
}

/**
 * Format status string for display (e.g., "not_started" -> "Not Started")
 */
export function formatStatus(status: string): string {
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Get color classes for programme status badge
 */
export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case "not_started":
      return "bg-yellow-100 text-yellow-800";
    case "in_progress":
      return "bg-teal-100 text-teal-800";
    case "completed":
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

/**
 * Get color classes for programme status badge (dark theme variant)
 */
export function getStatusColorDark(status: string): string {
  switch (status.toLowerCase()) {
    case "not_started":
      return "bg-yellow-500/20 text-yellow-400";
    case "in_progress":
      return "bg-teal-500/20 text-teal-400";
    case "completed":
      return "bg-green-500/20 text-green-400";
    default:
      return "bg-gray-500/20 text-gray-400";
  }
}
